// src/data/roommateTips.ts - Random tips for roommate pairing assistance

export interface RoommateTip {
  id: string;
  category: 'profile' | 'communication' | 'compatibility' | 'safety' | 'budget' | 'success';
  tip: string;
  icon?: string;
}

export const roommateTips: RoommateTip[] = [
  // Profile Optimization Tips
  {
    id: 'profile-1',
    category: 'profile',
    tip: 'Complete your profile to get 3x more matches! Users with complete profiles receive significantly more responses.',
    icon: '📝'
  },
  {
    id: 'profile-2',
    category: 'profile',
    tip: 'Add clear photos to your profile - profiles with photos get 5x more views.',
    icon: '📸'
  },
  {
    id: 'profile-3',
    category: 'profile',
    tip: 'Be specific about your budget range - it helps find better matches faster.',
    icon: '💰'
  },
  {
    id: 'profile-4',
    category: 'profile',
    tip: 'Update your preferences regularly - your needs may change over time.',
    icon: '🔄'
  },

  // Communication Tips
  {
    id: 'comm-1',
    category: 'communication',
    tip: 'Ask about living habits early in conversation - it saves time for both parties.',
    icon: '💬'
  },
  {
    id: 'comm-2',
    category: 'communication',
    tip: 'Be honest about your schedule - transparency leads to better matches.',
    icon: '⏰'
  },
  {
    id: 'comm-3',
    category: 'communication',
    tip: 'Respond to messages within 24 hours - active users get more matches.',
    icon: '⚡'
  },
  {
    id: 'comm-4',
    category: 'communication',
    tip: 'Ask thoughtful questions - show genuine interest in potential roommates.',
    icon: '❓'
  },

  // Compatibility Tips
  {
    id: 'comp-1',
    category: 'compatibility',
    tip: 'Similar sleep schedules improve compatibility - consider this when matching.',
    icon: '🌙'
  },
  {
    id: 'comp-2',
    category: 'compatibility',
    tip: 'Discuss cleanliness expectations upfront - it\'s a common source of conflict.',
    icon: '🧹'
  },
  {
    id: 'comp-3',
    category: 'compatibility',
    tip: 'Share your pet preferences early - it\'s important for many users.',
    icon: '🐾'
  },
  {
    id: 'comp-4',
    category: 'compatibility',
    tip: 'Consider social level compatibility - introverts and extroverts can coexist with clear boundaries.',
    icon: '👥'
  },

  // Safety Tips
  {
    id: 'safety-1',
    category: 'safety',
    tip: 'Always meet in public before committing - safety first!',
    icon: '🛡️'
  },
  {
    id: 'safety-2',
    category: 'safety',
    tip: 'Verify identity through the app\'s verification features.',
    icon: '✅'
  },
  {
    id: 'safety-3',
    category: 'safety',
    tip: 'Trust your instincts - if something feels off, it probably is.',
    icon: '🔍'
  },
  {
    id: 'safety-4',
    category: 'safety',
    tip: 'Share your plans with friends or family when meeting potential roommates.',
    icon: '👨‍👩‍👧‍👦'
  },

  // Budget & Logistics Tips
  {
    id: 'budget-1',
    category: 'budget',
    tip: 'Discuss budget expectations upfront - avoid surprises later.',
    icon: '💵'
  },
  {
    id: 'budget-2',
    category: 'budget',
    tip: 'Consider commute time when choosing location - it affects daily life.',
    icon: '🚇'
  },
  {
    id: 'budget-3',
    category: 'budget',
    tip: 'Plan move-in dates together - coordination is key.',
    icon: '📅'
  },
  {
    id: 'budget-4',
    category: 'budget',
    tip: 'Discuss utility costs and how they\'ll be split before moving in.',
    icon: '💡'
  },

  // Success Tips
  {
    id: 'success-1',
    category: 'success',
    tip: 'Be open-minded - sometimes the best matches are unexpected.',
    icon: '💫'
  },
  {
    id: 'success-2',
    category: 'success',
    tip: 'Focus on compatibility over perfection - no one is perfect!',
    icon: '✨'
  },
  {
    id: 'success-3',
    category: 'success',
    tip: 'Set clear boundaries from the start - it prevents future conflicts.',
    icon: '📋'
  },
  {
    id: 'success-4',
    category: 'success',
    tip: 'Remember that good communication is the foundation of any successful roommate relationship.',
    icon: '🤝'
  }
];

/**
 * Get a random tip from the tips database
 * @param category Optional category filter
 * @returns A random tip
 */
export const getRandomTip = (category?: RoommateTip['category']): RoommateTip => {
  const filteredTips = category 
    ? roommateTips.filter(tip => tip.category === category)
    : roommateTips;
  
  const randomIndex = Math.floor(Math.random() * filteredTips.length);
  return filteredTips[randomIndex];
};

/**
 * Get a random tip, excluding the current tip
 * @param currentTipId The ID of the current tip to exclude
 * @returns A random tip different from the current one
 */
export const getRandomTipExcluding = (currentTipId: string): RoommateTip => {
  const availableTips = roommateTips.filter(tip => tip.id !== currentTipId);
  const randomIndex = Math.floor(Math.random() * availableTips.length);
  return availableTips[randomIndex];
};

